/* eslint-disable react/jsx-indent-props */
import React, { useRef, useContext } from 'react';
import classNames from 'classnames';
import { QueryContext, QueryProvider } from '@thd-nucleus/data-sources';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { Heading } from '@thd-olt-component-react/heading';
import { Col, Row } from '@thd-olt-component-react/grid';
import { CustomProductPodDetails, ProductPodGroup } from '@thd-olt-component-react/product-pod-group';
import { MediaGalleryDesktop } from '@thd-olt-component-react/media-gallery';
import { GroupedPod } from './GroupedPod';
import { FabricOverview } from './FabricOverview';
import { useGroupedProducts } from '../../hooks/useGroupedProducts';
import './grouped-view.scss';
import { PriceRange } from '../price-range/PriceRange';
import { constants, collectionQueryOption } from '../CollectionHelpers';
import { GroupedViewPropTypes } from './GroupedViewPropTypes';

export const GroupedViewCollectionDesktop = ({
  collection, collectionId, storeId, skipFn,
}) => {
  const groupRefs = useRef([]);
  const { defaultVariables } = useContext(QueryContext);
  const { media, metadata, products } = collection;
  const { groups } = useGroupedProducts({ products });
  const {
    brandName, canonicalUrl, collectionDescription, name, productCount, roomType
  } = metadata || {};
  const { allProducts } = productCount || {};
  const isPatio = (roomType || '').toLowerCase() === 'patio';

  const centered = !media;
  const collectionDetailContainer = classNames(
    { 'collections_details--center': centered }
  );
  const freeDeliveryClass = classNames(
    'grouped-view-collection__free-delivery',
    { 'grouped-view-collection__free-delivery--centered': centered }
  );
  const smoothScrollToGroup = (groupIndex) => {
    const groupRef = groupRefs.current && groupRefs.current[groupIndex];
    if (groupRef) {
      groupRef.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return (
    <Row className="grouped-view-collection">
      <Row>
        <ErrorBoundary name="collections-details">
          {media && (
            <Col xs={6} className="grouped-view-collection__media_gallery">
              <MediaGalleryDesktop collectionId={collectionId} />
            </Col>
          )}
          <Col xs={media ? 6 : 12} className={collectionDetailContainer}>
            <div className="grouped-view-collection__details product-header">
              <div className="grouped-view-collection__details--group">
                <h1 className="product-header__collection-name product-header__collection-name--medium">
                  {name} {allProducts && (
                    <span className="header__count">({allProducts} Items)</span>
                  )}
                  <h2>
                    <span className="grouped-view-collection__details--details-text"> by &nbsp;
                      <a
                        className="product-header__brand "
                        href={canonicalUrl}
                      >{brandName}
                      </a>
                    </span>
                  </h2>
                </h1>
                <PriceRange products={products} priceFloat />
                {isPatio && (
                  <div className="collections-details__patio-messaging">
                    <span className="italic">
                      Prices may vary by fabric type and color. Cushion covers will be shipped separately.
                    </span>
                    <div className={freeDeliveryClass}>
                      <span className="u__bold alert-inline alert-inline--success">Free Delivery</span>
                      <span className="grouped-view-collection__free-delivery--spacer">on this collection</span>
                    </div>
                  </div>
                )}
              </div>
              <div>
                {collectionDescription && (
                  <div className="grouped-view-collection__details--group">
                    <p className="u__bold">About the Collection:</p>
                    {collectionDescription}
                  </div>
                )}
              </div>
              {isPatio && (

                <div className="grouped-view-collection__fabric-overview">
                  <FabricOverview />
                </div>
              )}
              <div className="grouped-view-collection__details--group">
                <p className="fabric-options__message">
                  <p className="u__bold">Samples:</p>
                  To see fabric options, please visit your local Home Depot store or purchase a fabric sample online.
                </p>
              </div>
              <div className="grouped-view-collection__details--group">
                <div>
                  <p className="return-policy__message">
                    <p className="u__bold"> Returns: </p>
                    Cushion slipcovers are custom and not returnable. Please refer to our
                    <a className="u__default-link" href={constants.RETURN_POLICY}> return policy </a>
                    for additional information.
                  </p>
                </div>
              </div>

            </div>
          </Col>
        </ErrorBoundary>
      </Row>
      <Row className="shop-the-collection">
        <Col>
          <ErrorBoundary name="shop-the-collection">
            <Heading centered title="Shop This Collection" underline />
            <QueryProvider
              dataSource="Collection"
              defaultVariables={{
                skipSubscribeAndSave: true,
                skipInstallServices: true,
                storeId,
                skipKPF: true,
                ...(typeof defaultVariables?.current?.isBrandPricingPolicyCompliant === 'boolean'
                  && { isBrandPricingPolicyCompliant:
                    defaultVariables.current.isBrandPricingPolicyCompliant })
              }}
              queryOptions={collectionQueryOption}
              skip={skipFn}
              cacheKey="groupedview-desktop"
              inherit="collections-page"
            >
              {groups.length > 0
            && (
              <>
                <Row className="shop-the-collection__groups">
                  {groups.map((group, groupIndex) => {
                    return (
                      <button
                        filter
                        type="button"
                        className="shop-the-collection__group bttn--filter"
                        key={group.name}
                        onClick={() => smoothScrollToGroup(groupIndex)}
                      >
                        <div className="shop-the-collection__group--text-wrap">
                          {group.name} ({group.products.length})
                        </div>
                      </button>
                    );
                  })}
                </Row>
                {groups.map((group, groupIndex) => {
                  return (
                    <Col key={group.name}>
                      <ErrorBoundary name={`shop-the-collection__${group.name}`}>
                        {/* eslint-disable-next-line no-return-assign */}
                        <div ref={(el) => (groupRefs.current[groupIndex] = el)} className="grid">
                          <Col>
                            <Heading
                              title={`${group.name} (${group.products.length})`}
                              underline
                              centered
                            />
                          </Col>
                          {group.products.map((product, productIndex) => {
                            const { identifiers } = product;
                            const { itemId } = identifiers || {};
                            return (
                              <Col fallback={12} sm={4} md={4} lg={4} key={productIndex}>
                                <GroupedPod
                                  key={productIndex}
                                  itemId={itemId}
                                  storeId={storeId}
                                  position={productIndex + 1}
                                  parent="collection"
                                />
                              </Col>
                            );
                          })}
                        </div>
                      </ErrorBoundary>
                    </Col>
                  );
                })}
              </>
            )}

              {!groups.length > 0 && (
                <Row>
                  <ProductPodGroup DetailsPod={CustomProductPodDetails}>
                    {(products || []).map((product, productIndex) => {
                      const { identifiers } = product;
                      const { itemId } = identifiers || {};
                      return (
                        <ErrorBoundary key={productIndex} name={`shop-the-collection__${product.itemId}`}>
                          <Col fallback={12} sm={4} md={4} lg={4}>
                            <GroupedPod
                              itemId={itemId}
                              storeId={storeId}
                              position={productIndex + 1}
                              hideLimitPerOrder
                              hidePriceBadge
                              parent="collection"
                            />
                          </Col>
                        </ErrorBoundary>
                      );
                    })}
                  </ProductPodGroup>
                </Row>
              )}
            </QueryProvider>
          </ErrorBoundary>
        </Col>
      </Row>
    </Row>
  );
};

GroupedViewCollectionDesktop.displayName = 'GroupedViewCollectionDesktop';

GroupedViewCollectionDesktop.propTypes = GroupedViewPropTypes;